import React, {useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {FormattedMessage, useIntl} from 'react-intl';

import {IStore} from 'redux/interface';
import {
  getTranslations,
  exerciseAsyncActions,
  getExerciseToDo,
  getExerciseDone,
  getExerciseStatistics
} from 'redux/pageAsyncActions';
import {useGetTranslations} from 'utils/hooks/useGetTranslations';
import {Pages, TranslationKeys} from 'utils/constants/routeConstants';

import XpeditionIcon from 'assets/image/menu-auth/xpedition.png';
import NotFoundInfo from '../../components/not-found-info';
import Loader from '../../components/loader';
import {useNavigate} from 'react-router-dom';
import {clearExerciseDialogue} from '../../redux/exercise/actions';
import {IExerciseDialogue} from '../../redux/exercise/interfaces';
import ExerciseCardSection from '../../components/exercise-card-section';
import {ExerciseCollapse} from './exercise-collapse/ExerciseCollapse';
import {ExerciseStats} from './exercise-stats/ExerciseStats';

import './styles.scss';

export const Exercise: React.FunctionComponent = () => {
  const {
    exerciseToDo,
    exerciseDone
  } = useSelector((state: IStore) => state.exercise);
  const {
    loading
  } = useSelector((state: IStore) => state.status);
  const {
    settings
  } = useSelector((state: IStore) => state.auth);
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(clearExerciseDialogue());
  }, []);

  const translationKey = useMemo(() => {
    const page = settings?.pages.find((p) => p.name.toLowerCase() === Pages.Exercises.toLowerCase());

    return page?.translationKey || TranslationKeys.Exercise;
  }, [settings]);

  useGetTranslations({translationKey});

  const title = useMemo(() => {
    const page = settings?.pages.find((p) => p.name.toLowerCase() === Pages.Exercises.toLowerCase());

    return page?.title || Pages.Exercises;
  }, [settings]);

  useEffect(() => {
    dispatch(exerciseAsyncActions[getExerciseToDo]());
    dispatch(exerciseAsyncActions[getExerciseDone]());
    dispatch(exerciseAsyncActions[getExerciseStatistics]());
  }, []);

  function handleOpenDialogue(dialogue: IExerciseDialogue) {
    navigate(`/exercise/${dialogue.dialogId}`);
  }

  function renderToDoDialogueCards() {
    return <div className="exercise__cards">
      {exerciseToDo.length === 0 ? <NotFoundInfo
        title={intl.formatMessage({
          id: 'gritx.exercise.sectionCards.notFound',
          defaultMessage: 'Sorry, but nothing can be found.'
        })}
      /> : <ExerciseCardSection
        showLoader={loading.includes(getExerciseToDo)}
        cards={exerciseToDo}
        onClickCard={handleOpenDialogue}
      />}
    </div>;
  }

  function renderDoneDialogueCards() {
    return <div className="exercise__cards">
      {exerciseDone.length === 0 ? <NotFoundInfo
        title={intl.formatMessage({
          id: 'gritx.exercise.sectionCards.notFound',
          defaultMessage: 'Sorry, but nothing can be found.'
        })}
      /> : <ExerciseCardSection
        showLoader={loading.includes(getExerciseDone)}
        cards={exerciseDone}
        onClickCard={handleOpenDialogue}
      />}
    </div>;
  }

  return <section className={'exercise container'}>
    {
      !loading.includes(getTranslations) && <>
        <h1 className="exercise__title">
          <img src={XpeditionIcon} alt="" className="exercise__icon"/>
          <FormattedMessage
            id={`gritx.common.navigation.${title}`}
            defaultMessage={title}
          />
        </h1>
        <ExerciseCollapse collapsed={true} title={intl.formatMessage({
          id: 'gritx.exercise.stats.title',
          defaultMessage: 'Statistics'
        })}>
          <ExerciseStats/>
        </ExerciseCollapse>
        <ExerciseCollapse collapsed={false} title={intl.formatMessage({
          id: 'gritx.exercise.todo.title',
          defaultMessage: 'To do'
        })}>
          <>
            {
              loading.includes(getExerciseToDo) ? <Loader/> : renderToDoDialogueCards()
            }
          </>
        </ExerciseCollapse>
        <ExerciseCollapse collapsed={true} title={intl.formatMessage({
          id: 'gritx.exercise.done.title',
          defaultMessage: 'Done'
        })}>
          <>
            {
              loading.includes(getExerciseDone) ? <Loader/> : renderDoneDialogueCards()
            }
          </>
        </ExerciseCollapse>
      </>
    }
  </section>;
};
